// Validation config for Customer fields (engine-supported rules only)
// Framework-agnostic for reuse across store and components
import type { ValidationConfig } from '../validationEngine/types';
import { customerAddressValidationConfig } from './addressValidationConfig';
import { removeFlowRules } from '../validationEngine/removeFlowRules';
import { calculateAge } from '../calculateAge/calculateAge';
import { customerHousingAndEmploymentValidationConfig } from './customerHousingAndEmploymentValidationConfig';

const NAME_REGEX = /^[A-Za-z '\\-]*$/;

export const customerValidationConfig: ValidationConfig = {
    business: {
        legalName: {
            rules: [
                { type: 'REQUIRED', messages: 'Business Name is required.' },
                { type: 'MAX_LENGTH', params: { max: 50 }, messages: 'Business Name must be under 50 characters' }
            ]
        },
        taxIdNumber: {
            rules: [
                { type: 'MIN_LENGTH', params: { min: 9 }, messages: 'Tax ID Number must be 9 characters' },
                { type: 'MAX_LENGTH', params: { max: 9 }, messages: 'Tax ID Number must be 9 characters' }
            ]
        },
        numberOfEmployees: {
            rules: [
                {
                    type: 'CUSTOM',
                    params: {
                        evaluator: (value: string) => {
                            const v = Number(value);
                            return v >= 0 && v <= 99999;
                        }
                    },
                    messages: 'Number of Employees must not exceed 99999'
                }
            ]
        },
        bankName: {
            rules: [{ type: 'MAX_LENGTH', params: { max: 20 }, messages: 'Bank Name must be under 20 characters' }]
        },
        bankContactName: {
            rules: [{ type: 'MAX_LENGTH', params: { max: 20 }, messages: 'Bank Contact Name must be under 20 characters' }]
        }
    },
    bankAccountNumber: {
        rules: [{ type: 'MAX_LENGTH', params: { max: 15 }, messages: 'Account number must be under 15 characters' }]
    },
    firstName: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'default|contract',
                conditions: { excludeDealModes: ['OptionalDesking'] },
                messages: {
                    inline: 'First Name is required.',
                    gcalert: 'Legal First Name is required.'
                }
            },
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 15 },
                messages: {
                    inline: 'First name can’t be longer than 15 characters.',
                    gcalert: 'Legal First Name cannot be longer than 15 characters.'
                }
            },
            { type: 'MAX_LENGTH', params: { max: 30 }, messages: 'Legal First Name must be under 30 characters.' },
            { type: 'REGEX', params: { regex: NAME_REGEX }, messages: "Use only letters and ' - " }
        ]
    },
    middleName: {
        rules: [
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 15 },
                messages: {
                    inline: 'Middle name can’t be longer than 15 characters.',
                    gcalert: 'Legal Middle Name cannot be longer than 15 characters.'
                }
            },
            { type: 'MAX_LENGTH', params: { max: 30 }, messages: 'Legal Middle Name must be under 30 characters' },
            { type: 'REGEX', params: { regex: NAME_REGEX }, messages: "Use only letters and ' - " }
        ]
    },
    lastName: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'default|contract',
                conditions: { excludeDealModes: ['OptionalDesking'] },
                messages: {
                    inline: 'Last Name is required.',
                    gcalert: 'Legal Last Name is required.'
                }
            },
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 25 },
                messages: {
                    inline: 'Last name can’t be longer than 25 characters.',
                    gcalert: 'Legal Last Name cannot be longer than 25 characters.'
                }
            },
            { type: 'MAX_LENGTH', params: { max: 30 }, messages: 'Legal Last Name must be under 30 characters.' },
            { type: 'REGEX', params: { regex: NAME_REGEX }, messages: "Use only letters and ' - " }
        ]
    },
    email: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'default|contract',
                conditions: { excludeDealModes: ['OptionalDesking'] },
                messages: 'Primary Email Address is required'
            },
            { type: 'MAX_LENGTH', params: { max: 50 }, messages: 'Primary Email Address must be valid and under 50 characters' },
            { type: 'REGEX', params: { regex: /^\S+@\S+\.\S+$/ }, messages: 'Primary Email Address is invalid' },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string, ctx: any) => {
                        if (!value) return true;
                        const current = String(value).trim().toLowerCase();
                        const other = String(ctx?.otherEmail || '')
                            .trim()
                            .toLowerCase();
                        return !other || current !== other;
                    }
                },
                messages: {
                    inline: 'Buyer and Co-buyer email addresses cannot be identical.'
                }
            }
        ]
    },
    emailAlternative: {
        rules: [
            { type: 'MAX_LENGTH', params: { max: 50 }, messages: 'Secondary Email Address must be valid and under 50 characters' },
            { type: 'REGEX', params: { regex: /^\S+@\S+\.\S+$/ }, messages: 'Secondary Email Address is invalid' }
        ]
    },
    title: {
        rules: [
            {
                type: 'MAX_LENGTH',
                flow: 'default|contract',
                params: { max: 25 },
                conditions: {
                    when: (ctx: any, _value?: unknown, scope?: unknown) =>
                        (ctx?.customerType || (scope as any)?.customerType) === 'Business'
                },
                messages: 'Title must be under 25 characters.'
            }
        ]
    },
    ...customerAddressValidationConfig,
    billingAddress: {
        $group: {
            ...removeFlowRules(customerAddressValidationConfig, 'contract')
        }
    },

    // SPD specific fields
    socialSecurityNumber: {
        rules: [
            { type: 'REGEX', params: { regex: /^\d+$/ }, messages: 'Social Security Number is not valid' },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const v = String(value ?? '');
                        return v.length === 9;
                    }
                },
                messages: 'Social Security Number must be 9 digits'
            }
        ]
    },
    driversLicenseExpirationDate: {
        rules: [
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const enteredDate = new Date(value);
                        return !Number.isNaN(enteredDate.getTime());
                    }
                },
                messages: 'Please enter a valid expiration date'
            },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const enteredDate = new Date(value);
                        if (Number.isNaN(enteredDate.getTime())) return true;
                        return normalizeToDateOnly(enteredDate) >= normalizeToDateOnly(new Date());
                    }
                },
                messages: 'Date must be future date'
            }
        ]
    },
    dateOfBirth: {
        rules: [
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: {
                    evaluator: (value: string) => {
                        if (!value) return true;
                        const d = new Date(value);
                        if (Number.isNaN(d.getTime())) return false;
                        const now = new Date();
                        if (d >= now) return false;
                        const hundredYearsAgo = new Date(now.getFullYear() - 100, now.getMonth(), now.getDate());
                        return d >= hundredYearsAgo;
                    }
                },
                messages: 'Date of Birth must be prior to current date and within the last 100 years.'
            },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const enteredDate = new Date(value);
                        return !Number.isNaN(enteredDate.getTime());
                    }
                },
                messages: 'Please enter a valid date'
            },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const enteredDate = new Date(value);
                        if (Number.isNaN(enteredDate.getTime())) return true;
                        return normalizeToDateOnly(enteredDate) <= normalizeToDateOnly(new Date());
                    }
                },
                messages: 'Age must not be future date'
            },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const enteredDate = new Date(value);
                        if (Number.isNaN(enteredDate.getTime())) return true;
                        return calculateAge(enteredDate, new Date()) >= 18;
                    }
                },
                messages: 'Age must be at least 18 years'
            },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: string) => {
                        const enteredDate = new Date(value);
                        if (Number.isNaN(enteredDate.getTime())) return true;
                        return calculateAge(enteredDate, new Date()) <= 100;
                    }
                },
                messages: 'Age must be less than or equal to 100 years' //Finance Services only accepts up to 100 years
            }
        ]
    },
    ...customerHousingAndEmploymentValidationConfig
};

const normalizeToDateOnly = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
